// Same loader and create/start protocol as the official @beefree.io/sdk wrapper.
// https://github.com/BeefreeSDK/beefree-sdk-npm-official/blob/master/src/index.ts
export const BEEFREE_SCRIPT_URL = 'https://app-rsrc.getbee.io/plugin/v2/BeePlugin.js';

let loadingPlugin;

export const loadBeefreePlugin = (win = window, doc = document, timeoutMs = 30000) => {
  if (win.BeePlugin?.create) return Promise.resolve(win.BeePlugin);
  if (loadingPlugin) return loadingPlugin;

  loadingPlugin = new Promise((resolve, reject) => {
    const script = doc.createElement('script');
    let settled = false;
    const finish = (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      script.onload = null;
      script.onerror = null;
      if (error) {
        script.remove();
        reject(error);
      } else {
        resolve(win.BeePlugin);
      }
    };
    const timer = setTimeout(() => finish(new Error('Beefree did not load. Check the connection and allowed external resources.')), timeoutMs);
    script.async = true;
    script.src = BEEFREE_SCRIPT_URL;
    script.onload = () => finish(win.BeePlugin?.create ? null : new Error('Beefree could not be initialized.'));
    script.onerror = () => finish(new Error('Beefree could not be loaded. Check the connection and allowed external resources.'));
    doc.head.appendChild(script);
  }).catch((error) => {
    loadingPlugin = null;
    throw error;
  });
  return loadingPlugin;
};

export const parseBeefreeExport = (jsonFile, htmlFile) => {
  let design;
  try {
    design = typeof jsonFile === 'string' ? JSON.parse(jsonFile) : JSON.parse(JSON.stringify(jsonFile));
  } catch (_) {
    throw new Error('Beefree returned an invalid design. Your template was not saved.');
  }
  if (!design?.page || !Array.isArray(design.page.rows) || typeof htmlFile !== 'string' || !htmlFile.trim()) {
    throw new Error('Beefree returned an incomplete export. Your template was not saved.');
  }
  return { design, html: htmlFile };
};

// There is no request ID in onSave. After a timeout, forbid another request on
// this instance so a late callback cannot save an unrelated, older export.
export const createBeefreeExportBridge = (invokeSave, timeoutMs = 30000) => {
  let pending = null;
  let unavailable = false;
  const rejectPending = (error) => {
    if (!pending) return;
    const current = pending;
    pending = null;
    clearTimeout(current.timer);
    current.reject(error);
  };

  return {
    request() {
      if (unavailable) return Promise.reject(new Error('The editor export session is unavailable. Keep this page open to preserve unsaved work.'));
      if (pending) return Promise.reject(new Error('An export is already in progress.'));
      return new Promise((resolve, reject) => {
        const current = { resolve, reject, timer: null };
        pending = current;
        current.timer = setTimeout(() => {
          unavailable = true;
          rejectPending(new Error('Beefree export timed out. Your template was not saved. Keep this page open.'));
        }, timeoutMs);
        try {
          const result = invokeSave();
          // Completion is exclusively onSave, not the method's return value.
          if (result?.catch) result.catch(() => {
            if (pending === current) {
              unavailable = true;
              rejectPending(new Error('Beefree could not export this template.'));
            }
          });
        } catch (_) {
          unavailable = true;
          rejectPending(new Error('Beefree could not export this template.'));
        }
      });
    },
    onSave(jsonFile, htmlFile) {
      // An unavailable session consumes late callbacks without saving them.
      // Only an idle, usable session may offer a native toolbar Save to the host.
      if (unavailable) return true;
      if (!pending) return false;
      const current = pending;
      pending = null;
      clearTimeout(current.timer);
      try { current.resolve(parseBeefreeExport(jsonFile, htmlFile)); } catch (error) { current.reject(error); }
      return true;
    },
    fail(message = 'Beefree is unavailable. Your unsaved work has not been saved.') {
      unavailable = true;
      rejectPending(new Error(message));
    },
    dispose() {
      unavailable = true;
      rejectPending(new Error('The editor has been closed.'));
    }
  };
};

export const createRevisionTracker = (persisted = false) => {
  let revision = 0;
  let savedRevision = persisted ? 0 : -1;
  return {
    change: () => ++revision,
    snapshot: () => revision,
    saved: (exportRevision) => { savedRevision = exportRevision; return revision !== savedRevision; },
    dirty: () => revision !== savedRevision
  };
};
